import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  LinearProgress,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { H1, H4, Paragraph, H6, Small, Tiny } from "components/Typography";
import { colors, formatCurrency } from "common";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import modules from "modules";
import { useAppDispatch } from "hooks/use-app-dispatch";
import { useAppSelector } from "hooks/use-app-selector";
import { AffiliateInfoCard } from "components/card/affiliate";
import { unwrapResult } from "@reduxjs/toolkit";
import { userProfileClass } from "modules/pcw-profile";
import { useRouter } from "next/router";
import { navigationRoutes } from "common/constants/navigation-routes";
import NothingToShow from "components/worth-it-builder/NothingToShow";
import WestIcon from "@mui/icons-material/West";
import useResolution from "hooks/useResolution";

const { actions: PcwProfileActions } = modules.pcwUserProfile;

const { actions: userAffiliateActions, selectors: userAffiliateSelectors } =
  modules.userAffiliate;

const Affiliate = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { isMobile } = useResolution();
  const [isAffiliate, setIsAffiliate] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const getUserAffiliateDetailsPayload = useAppSelector(
    userAffiliateSelectors.selectGetUserAffiliateDetailsPayload
  );
  const userAffiliateVouchers = useAppSelector(
    userAffiliateSelectors.selectUserAffiliateVouchers
  );
  const userAffiliateDetails = useAppSelector(
    userAffiliateSelectors.selectUserAffiliateDetails
  );
  const isUserAffiliateDetailsLoading = useAppSelector(
    userAffiliateSelectors.selectIsUserAffiliateDetailsLoading
  );

  const voucherAffiliateOptions = useMemo(() => {
    return userAffiliateVouchers?.map((voucher) => ({
      label: `${voucher?.code} (${voucher?.name})`,
      value: voucher?.code,
    }));
  }, [userAffiliateVouchers]);

  const selectedVoucherOption = useMemo(() => {
    return voucherAffiliateOptions?.find(
      (option) => option.value === getUserAffiliateDetailsPayload?.voucher_code
    );
  }, [getUserAffiliateDetailsPayload?.voucher_code, voucherAffiliateOptions]);

  const getInitialData = () => {
    dispatch(userAffiliateActions.getUserAffiliateVouchersThunk());
    dispatch(userAffiliateActions.getUserAffiliateDetailsThunk());
  };

  const onClickSearch = () => {
    dispatch(userAffiliateActions.getUserAffiliateDetailsThunk());
  };

  const updateGetDetailsPayload = (key: string, payload: string) => {
    dispatch(
      userAffiliateActions.updateGetDetailsPayload({ [key]: payload || "" })
    );
  };

  const getProfile = useCallback(async () => {
    try {
      const response = unwrapResult(
        await dispatch(
          PcwProfileActions?.pcwUserProfileThunk({
            class: userProfileClass.all,
          })
        )
      );

      if (response?.success) {
        //call only if affiliate user
        getInitialData();

        setIsAffiliate(response?.originalData?.data?.affiliate === 1);
        setIsLoading(false);
      } else {
        setIsAffiliate(false);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    getProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isAffiliate) {
      router.push(navigationRoutes?.homepage);
    }
  }, [isAffiliate, router]);

  if (isLoading || isUserAffiliateDetailsLoading || isAffiliate === false) {
    return <LinearProgress />;
  }

  return (
    <div title="PC Worth Affiliate (BETA)">
      <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <H1>
            <span style={{ color: "#0c304a" }}>PC </span>{" "}
            <span style={{ color: "#cf151a" }}>Worth</span>{" "}
            {isMobile ? <br /> : " "}
            <span style={{ color: "#000000" }}>Affiliate Program</span> (BETA)
          </H1>
          <Button
            onClick={() => {
              window.location.href = "/profile";
            }}
            variant="contained"
            color="error"
          >
            <WestIcon />
          </Button>
        </div>

        <Card style={{ marginTop: 10 }}>
          <CardContent style={{ marginBottom: -6 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <Autocomplete
                  multiple={false}
                  options={voucherAffiliateOptions}
                  value={selectedVoucherOption?.value}
                  renderInput={(params) => (
                    <TextField {...params} label="Voucher Code" />
                  )}
                  onChange={(event, _newValue, _reason, details) => {
                    updateGetDetailsPayload(
                      "voucher_code",
                      details?.option?.value || ""
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={5}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label="Start Date"
                      value={dayjs(
                        getUserAffiliateDetailsPayload?.sold_date_from
                      )}
                      maxDate={dayjs(Date.now())}
                      onChange={(newValue) =>
                        updateGetDetailsPayload(
                          "sold_date_from",
                          newValue?.format("YYYY-MM-DD").toString()
                        )
                      }
                    />
                    <Paragraph mt={0.5}>-</Paragraph>
                    <DatePicker
                      sx={{ width: "100%" }}
                      label="End Date"
                      value={dayjs(
                        getUserAffiliateDetailsPayload?.sold_date_to
                      )}
                      maxDate={dayjs(Date.now())}
                      onChange={(newValue) =>
                        updateGetDetailsPayload(
                          "sold_date_to",
                          newValue?.format("YYYY-MM-DD").toString()
                        )
                      }
                    />
                  </Box>
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={2}>
                <Button
                  onClick={onClickSearch}
                  color="secondary"
                  fullWidth
                  variant="contained"
                >
                  Search
                </Button>
              </Grid>
            </Grid>
          </CardContent>
          {isUserAffiliateDetailsLoading ? <LinearProgress /> : null}
        </Card>

        <div>
          <Grid container spacing={1.5} mt={1}>
            <Grid item xs={4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: "center" }}>
                  <H4>{userAffiliateDetails?.data?.total_customers}</H4>
                  <Small>Total Customers</Small>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card elevation={3}>
                <CardContent sx={{ textAlign: "center" }}>
                  <H4 sx={{ color: colors.success, fontWeight: 700 }}>
                    {formatCurrency(
                      userAffiliateDetails?.data?.total_incentives
                    )}
                  </H4>
                  <Small>Total Incentives</Small>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={4}>
              <Card elevation={2}>
                <CardContent sx={{ textAlign: "center" }}>
                  <H4>
                    {formatCurrency(userAffiliateDetails?.data?.total_discount)}
                  </H4>
                  <Small>Referral Savings</Small>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Box
            mt={5}
            maxHeight={550}
            style={{ overflowY: "auto" }}
            paddingRight={1}
          >
            {userAffiliateDetails?.data?.orders &&
            userAffiliateDetails?.data?.orders[0] ? (
              <>
                {userAffiliateDetails?.data?.orders.map((order, index) => (
                  <AffiliateInfoCard key={index} order={order} />
                ))}
              </>
            ) : (
              <NothingToShow
                image={"/assets/images/kachi/kachi-nervous.png"}
                title={isLoading ? "Loading..." : "No Result Found"}
                subtitle={"No customer found for current month"}
              />
            )}
          </Box>
        </div>
      </Container>
    </div>
  );
};

export default Affiliate;
